import { Button, Container, Stack, Typography } from '@mui/material';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import { FormattedMessage } from 'react-intl';
import { useAuth } from 'react-oidc-context';
import {
  Link,
  useLocation,
  useRouteError,
  useSearchParams,
} from 'react-router-dom';
import ErrorDisplay from '@product-framework/components/ErrorDisplay';
import { Footer, Logo } from '@product-framework/index';
import {
  clearAuthTokens,
  useLocalization,
  useStore,
  Page,
} from '@ui-framework/index';
import getDomain from '@utils/GetDomain';

export const ERROR_PAGE_TYPE = {
  NOT_FOUND: 'NOT_FOUND',
  AUTH_ERROR: 'AUTH_ERROR',
  FORBIDDEN: 'FORBIDDEN',
  CODE_ERROR: 'CODE_ERROR',
  SERVICE_DOWN: 'SERVICE_DOWN',
  SERVER_ERROR: 'SERVER_ERROR',
};

export function getErrorMessage({
  type = ERROR_PAGE_TYPE.CODE_ERROR,
  redirectArgs,
  errors = null,
  formatMessage = () => undefined,
}) {
  const auth = useAuth();
  const store = useStore();

  const goToLogin = async () => {
    await store.clearStoredData();
    await auth?.removeUser();
    await clearAuthTokens(async () => {
      let logoutUrl = getDomain(process.env.REACT_APP_API_HOST, '', false);
      const idToken = auth?.user?.id_token;
      if (redirectArgs?.redirect_uri) {
        logoutUrl = `${logoutUrl}/?redirect_url=${encodeURIComponent(
          redirectArgs?.redirect_uri,
        )}`;
      }

      if (auth?.signoutSilent) {
        await auth.signoutSilent({
          post_logout_redirect_uri: logoutUrl,
          ...(idToken && { id_token_hint: idToken }),
          silentRequestTimeoutInSeconds: 2,
        });
      }
      window.location.href = logoutUrl;
    });
  };

  const errorPageButtons = (
    <div>
      <Typography sx={{ mt: 2, mb: 2 }} variant="body2">
        Please review our <Link to="/public/help">Help Documentation</Link> for
        troubleshooting steps and additional details or you can try the below
        options:
      </Typography>
      {/* <Typography>You can try the below options: </Typography> */}
      <Stack direction="row" spacing={1.5} sx={{ mt: 2 }}>
        <Button variant="outlined" onClick={() => window.history.back()}>
          <FormattedMessage id="BACK_BUTTON" defaultMessage="Back" />
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            goToLogin();
          }}>
          <FormattedMessage
            id="GOTO_LOGIN_BUTTON"
            defaultMessage="Go to Login"
          />
        </Button>
      </Stack>
    </div>
  );

  const errorMessages = {
    NOT_FOUND: {
      text: formatMessage({
        id: 'RESOURCE_NOT_FOUND',
        defaultMessage: 'Resource not found',
      }),
      subText: formatMessage({
        id: 'RESOURCE_NOT_FOUND_SUBTEXT',
        defaultMessage:
          'We could not find the resource you were looking for. You can try going back or to the dashboard or logging out.',
      }),
      errorMessage: errors?.routeError?.message,
      buttonComponent: errorPageButtons,
    },
    SERVER_ERROR: {
      text: formatMessage({
        id: 'UNPROCESSABLE_REQUEST',
        defaultMessage: 'Unprocessable request',
      }),
      subText: formatMessage({
        id: 'UNPROCESSABLE_REQUEST_SUBTEXT',
        defaultMessage:
          'The server is unable to process your request at this time. You can try going back or to the dashboard or logging out.',
      }),
      errorMessage: errors?.serverError?.message
        ? decodeURIComponent(errors?.serverError?.message)
        : null,
      buttonComponent: errorPageButtons,
    },
    SERVICE_DOWN: {
      text: formatMessage({
        id: 'SERVICE_UNAVAILABLE',
        defaultMessage: 'Service Unavailable',
      }),
      subText: formatMessage({
        id: 'SERVICE_UNAVAILABLE_SUBTEXT',
        defaultMessage:
          'The resource you are trying to access is unavailable at this time. Please retry the action after some time.',
      }),
      errorMessage: errors?.networkError?.message
        ? decodeURIComponent(errors?.networkError?.message)
        : null,
      buttonComponent: errorPageButtons,
    },
    AUTH_ERROR: {
      text: formatMessage({
        id: 'AUTHENTICATION_ERROR',
        defaultMessage: 'Authentication Error',
      }),
      subText: formatMessage({
        id: 'AUTHENTICATION_ERROR_SUBTEXT',
        defaultMessage:
          'We are unable to authenticate your action currently. Please login and retry the action.',
      }),
      errorMessage: errors?.authError?.message,
      buttonComponent: errorPageButtons,
    },
    FORBIDDEN: {
      text: formatMessage({
        id: 'UNAUTHORIZED_ACCESS',
        defaultMessage: 'UnAuthorized Access',
      }),
      subText: formatMessage({
        id: 'UNAUTHORIZED_ACCESS_SUBTEXT',
        defaultMessage:
          'You do not have permissions to access this resource. Please login and retry the action.',
      }),
      errorMessage: formatMessage({
        id: 'ACCESS_DENIED',
        defaultMessage: 'Access Denied',
      }),
      buttonComponent: errorPageButtons,
    },
    CODE_ERROR: {
      text: formatMessage({
        id: 'OOPS_SOMETHING_WENT_WRONG',
        defaultMessage: 'Oops! Something went wrong.',
      }),
      subText: formatMessage({
        id: 'OOPS_SOMETHING_WENT_WRONG_SUBTEXT',
        defaultMessage:
          'We are unable to process your request at this time. You can try going back or to the dashboard or logging out.',
      }),
      errorMessage: errors?.codeErrors?.message || 'Unknown',
      buttonComponent: errorPageButtons,
    },
  };
  const errorDescriptionLabel = formatMessage({
    id: 'ERROR_DESCRIPTION',
    defaultMessage: 'Error Description:',
  });

  const messageToDisplay = `${errorDescriptionLabel} ${errorMessages[type]?.errorMessage}`;
  errorMessages[type].errorMessage = messageToDisplay;

  return errorMessages[type];
}

function ErrorPage({
  type = ERROR_PAGE_TYPE.CODE_ERROR,
  codeErrors = null,
  redirectUri = '',
}) {
  const auth = useAuth();
  const { formatMessage } = useLocalization();
  const routeError = useRouteError();
  const { state } = useLocation();

  let redirectArgs = {
    redirect_uri: '/dashboard',
  };

  if (state?.redirect_uri) {
    redirectArgs = { redirect_uri: state?.redirect_uri };
  }

  if (redirectUri) {
    redirectArgs = { redirect_uri: redirectUri };
  }

  const authError = auth?.error;

  const [searchParams] = useSearchParams();
  const networkErrorTxt = searchParams.get('networkError');
  const networkError = networkErrorTxt ? JSON.parse(networkErrorTxt) : null;

  const serverErrorTxt = searchParams.get('serverError');
  const serverError = serverErrorTxt ? JSON.parse(serverErrorTxt) : null;

  return (
    <Page>
      <Container
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
        }}>
        <Grid2 container>
          <Grid2 xs={12} sx={{ mt: 3 }}>
            <Logo href="/" />
          </Grid2>
          <Grid2 xs={12}>
            <ErrorDisplay
              {...getErrorMessage({
                type,
                redirectArgs,
                errors: {
                  routeError,
                  authError,
                  networkError,
                  serverError,
                  codeErrors,
                },
                formatMessage,
              })}
            />
          </Grid2>
        </Grid2>
        <Footer maxWidth="xl" />
      </Container>
    </Page>
  );
}

export default ErrorPage;
